import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REGULARS, type Difficulty } from '../../ai';
import {
  CampaignError,
  abandonRun,
  buyAtCounter,
  closeRun,
  fieldTable,
  finishTable,
  headStartFor,
  recordAction,
  runDeck,
  startRun,
  tradeablePawns,
  type FieldedPawn,
  type Profile,
  type Purchase,
  type RunState,
  type TableOutcome,
} from '../../campaign';
import {
  DEFAULT_CONSTANTS,
  FILES,
  type Action,
  type CardSpec,
  type MatchEvent,
  type Replay,
} from '../../engine';
import { t } from '../../i18n';
import type { AiClient } from '../../app/aiClient';
import { clearProfile, loadProfile, newProfile, saveProfile } from '../../app/campaignStore';
import { guideSeen, markGuideSeen } from '../../app/guides';
import { CampaignGuide } from '../components/CampaignGuide';
import { faceLong } from '../../app/describe';
import { MatchHost } from '../../app/matchHost';
import { MatchLogger, hostSource } from '../../app/matchLog';
import type { Settings } from '../../app/settings';
import { CardView } from '../components/CardView';
import { ConfirmDialog } from '../components/MatchDialogs';
import { MatchScreen } from './MatchScreen';

const C = DEFAULT_CONSTANTS;
const TABLE_DIFFICULTY: readonly Difficulty[] = ['standard', 'standard', 'hard'];

const regularName = (table: number): string =>
  REGULARS[(table - 1) % REGULARS.length]?.name ?? 'The Broker';
const specName = (c: CardSpec): string => (c.kind === 'wild' ? t('card.wild') : faceLong(c.face));
const pawnName = (p: Profile, id: string, standIn: boolean): string =>
  standIn ? `${t('campaign.standIn')} ${id}` : (p.pawns[id]?.name ?? id);

function Records({ p, id }: { p: Profile; id: string }) {
  const r = p.pawns[id]?.records;
  if (!r) return null;
  return (
    <span className="dim small">
      {t('campaign.records', {
        tables: r.tablesPlayed,
        captures: r.captures,
        captured: r.timesCaptured,
        epitaphs: r.epitaphsLeft,
        best: r.bestChain,
      })}
    </span>
  );
}

export function CampaignScreen({
  ai,
  delay,
  settings,
  reduceMotion,
  onExit,
  onExportReplay,
}: {
  ai: AiClient;
  delay: (action?: Action) => number;
  settings: Settings;
  reduceMotion: boolean;
  /** Leaves the campaign; `notice` says why the latest save failed, for the menu to show. */
  onExit: (notice?: string) => void;
  onExportReplay: (r: Replay) => void;
}) {
  const [profile, setProfileState] = useState<Profile>(() => {
    const loaded = loadProfile();
    if (loaded) return loaded;
    const created = newProfile();
    saveProfile(created);
    return created;
  });
  const profileRef = useRef(profile);
  const [error, setError] = useState<string | null>(null);
  // Why the latest save failed, until one works again: shown at the table too, and on leaving.
  const [saveError, setSaveError] = useState<string | null>(null);
  // The guide opens by itself on the first visit, and from the header at any time.
  const [guide, setGuide] = useState(() => !guideSeen('campaign'));
  const [confirm, setConfirm] = useState<'abandon' | 'reset' | null>(null);
  const [host, setHost] = useState<MatchHost | null>(null);
  const [showResult, setShowResult] = useState(false);

  const setProfile = useCallback((next: Profile) => {
    profileRef.current = next;
    setProfileState(next);
    const err = saveProfile(next);
    const failed = err ? t('error.storage', { message: err }) : null;
    setSaveError(failed);
    if (failed) setError(failed);
  }, []);

  const apply = (fn: (p: Profile) => Profile) => {
    // The last message goes first: one this action raises (a failed save) is then still shown.
    setError(null);
    try {
      setProfile(fn(profileRef.current));
    } catch (e) {
      setError(e instanceof CampaignError ? e.message : t('error.generic', { message: String(e) }));
    }
  };

  /** Opens (or resumes) the table match stored in the run. */
  const openTable = useCallback(
    (p: Profile): MatchHost | null => {
      const run = p.run;
      if (!run?.match) return null;
      const match = run.match;
      const table = run.table;
      const events: MatchEvent[] = [];
      let logger: MatchLogger | null = null;
      const h = new MatchHost(
        {
          config: match.config,
          seed: match.seed,
          controllers: {
            player: { kind: 'human', name: t('match.you') },
            [match.config.seating.players[1]]: {
              kind: 'ai',
              name: regularName(table),
              difficulty: TABLE_DIFFICULTY[table - 1] ?? 'standard',
              personality: REGULARS[match.regular % REGULARS.length],
            },
          },
        },
        {
          ai,
          delay,
          actions: match.actions,
          onAction: (_before, action, after, evts) => {
            events.push(...evts);
            let next = recordAction(profileRef.current, action);
            if (after.phase === 'MATCH_OVER' && after.result) {
              const full = [...setupEvents, ...events];
              next = finishTable(next, {
                result: after.result,
                scores: { S: after.players.S.totalScore, N: after.players.N.totalScore },
                collateralUsed: after.players.S.collateralUsed,
                events: full,
              });
            }
            setProfile(next);
            logger?.afterAction(evts);
          },
          onDispose: () => logger?.leave(),
        },
      );
      // Each table has its own seed, so it identifies the match across visits.
      logger = new MatchLogger(
        hostSource(h, {
          id: `campaign-${match.seed}`,
          mode: 'campaign',
          startedAt: new Date().toISOString(),
          campaign: { run: run.number, table, regular: regularName(table) },
        }),
      );
      // Events already played (resume) count toward the records too.
      const setupEvents: MatchEvent[] = h.getSnapshot().log.slice();
      setHost(h);
      return h;
    },
    [ai, delay, setProfile],
  );

  // Resume a table in progress when the screen opens. The cleanup closes that host, so the extra
  // mount React StrictMode performs in development never leaves a second table running.
  useEffect(() => {
    const p = profileRef.current;
    if (p.run?.phase !== 'playing' || !p.run.match) return;
    const resumed = openTable(p);
    return () => resumed?.dispose();
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => host?.dispose(), [host]);

  const run = profile.run;

  // ---- Table in progress ----------------------------------------------------------------------
  if (host && (run?.phase === 'playing' || !showResult)) {
    const table = run?.table ?? 1;
    return (
      <MatchScreen
        host={host}
        settings={settings}
        reduceMotion={reduceMotion}
        exitLabel={t('action.next')}
        title={`${t('campaign.table', { table, tables: C.CAMPAIGN_TABLES })} · ${regularName(table)}`}
        showMarks
        saveWarning={saveError}
        onExit={() => {
          const over = host.getSnapshot().view.phase === 'MATCH_OVER';
          host.dispose();
          setHost(null);
          setShowResult(over);
          if (!over) onExit(saveError ?? undefined);
        }}
        onSaveAndExit={() => {
          host.dispose();
          setHost(null);
          onExit(saveError ?? undefined);
        }}
        onExport={() => onExportReplay(host.replay)}
      />
    );
  }

  const outcome: TableOutcome | undefined = run?.outcomes[run.outcomes.length - 1];

  return (
    <main className="page page-campaign">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={() => onExit()}>
          ← {t('menu.back')}
        </button>
        <h1>{t('campaign.title')}</h1>
        <button type="button" className="btn btn-small" onClick={() => setGuide(true)}>
          {t('campaign.howItWorks')}
        </button>
      </header>
      {guide && (
        <CampaignGuide
          onClose={() => {
            markGuideSeen('campaign');
            setGuide(false);
          }}
        />
      )}
      {error && (
        <p className="notice" role="alert">
          {error}
        </p>
      )}
      {showResult && outcome ? (
        <TableResult profile={profile} outcome={outcome} onContinue={() => setShowResult(false)} />
      ) : !run ? (
        <Hub
          // A new profile has a new stable: start its roster choice afresh.
          key={profile.seed}
          profile={profile}
          onStart={(ids) => apply((p) => startRun(p, ids))}
          onReset={() => setConfirm('reset')}
        />
      ) : run.phase === 'fielding' ? (
        <Fielding
          profile={profile}
          run={run}
          onField={(fielding) => {
            try {
              const next = fieldTable(profileRef.current, fielding);
              setProfile(next);
              openTable(next);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            }
          }}
          onAbandon={() => setConfirm('abandon')}
        />
      ) : run.phase === 'playing' ? (
        <div className="panel">
          <p>{t('campaign.table', { table: run.table, tables: C.CAMPAIGN_TABLES })}</p>
          <button type="button" className="btn btn-primary" onClick={() => openTable(profile)}>
            {t('campaign.continueRun')}
          </button>
        </div>
      ) : run.phase === 'counter' ? (
        <Counter
          profile={profile}
          run={run}
          onLeave={(purchases) => apply((p) => buyAtCounter(p, purchases))}
          onAbandon={() => setConfirm('abandon')}
        />
      ) : (
        <RunEnd profile={profile} run={run} onClose={() => apply(closeRun)} />
      )}
      {confirm === 'abandon' && (
        <ConfirmDialog
          title={t('campaign.abandonRun')}
          body={t('campaign.abandonConfirm')}
          confirmLabel={t('campaign.abandonRun')}
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            apply(abandonRun);
          }}
        />
      )}
      {confirm === 'reset' && (
        <ConfirmDialog
          title={t('campaign.resetProfile')}
          body={t('campaign.resetConfirm')}
          confirmLabel={t('campaign.resetProfile')}
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            clearProfile();
            setProfile(newProfile());
          }}
        />
      )}
    </main>
  );
}

function Hub({
  profile,
  onStart,
  onReset,
}: {
  profile: Profile;
  onStart: (ids: string[]) => void;
  onReset: () => void;
}) {
  const needed = Math.min(C.CAMPAIGN_FIELDED, profile.stable.length);
  const [chosen, setChosen] = useState<string[]>(() => profile.stable.slice(0, needed));
  const toggle = (id: string) =>
    setChosen((c) =>
      c.includes(id) ? c.filter((x) => x !== id) : c.length < needed ? [...c, id] : c,
    );
  const standIns = C.CAMPAIGN_FIELDED - needed;
  return (
    <div className="campaign-hub">
      <p className="lead">{t('campaign.intro')}</p>
      <p className="dim">
        {t('campaign.stats', {
          started: profile.stats.runsStarted,
          won: profile.stats.runsWon,
          tables: profile.stats.tablesWon,
        })}
      </p>
      <section className="panel">
        <h2>{t('campaign.stable')}</h2>
        <p className="dim">
          {standIns > 0
            ? t('campaign.chooseRosterStandIns', { n: needed, m: standIns })
            : t('campaign.chooseRoster', { n: needed })}
        </p>
        <ul className="pawn-list">
          {profile.stable.map((id) => (
            <li key={id}>
              <label className={`pawn-pick ${chosen.includes(id) ? 'is-checked' : ''}`}>
                <input type="checkbox" checked={chosen.includes(id)} onChange={() => toggle(id)} />
                <strong>{profile.pawns[id]?.name}</strong>
                <Records p={profile} id={id} />
              </label>
            </li>
          ))}
        </ul>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary btn-large"
            disabled={chosen.length !== needed}
            onClick={() => onStart(chosen)}
          >
            {t('campaign.newRun')}
          </button>
        </div>
      </section>
      <section className="panel">
        <h2>{t('campaign.collection')}</h2>
        {profile.collection.length === 0 ? (
          <p className="dim">{t('campaign.collectionEmpty')}</p>
        ) : (
          <ul className="pawn-list compact">
            {profile.collection.map((id) => (
              <li key={id}>
                <strong>{profile.pawns[id]?.name}</strong> <Records p={profile} id={id} />
              </li>
            ))}
          </ul>
        )}
      </section>
      {profile.history.length > 0 && (
        <section className="panel">
          <h2>{t('campaign.history')}</h2>
          <ul className="pawn-list compact">
            {[...profile.history]
              .reverse()
              .slice(0, 8)
              .map((h) => (
                <li key={h.number}>
                  {t('campaign.historyItem', {
                    n: h.number,
                    tables: h.tablesWon,
                    outcome: h.won
                      ? t('campaign.history.won')
                      : h.abandoned
                        ? t('campaign.history.abandoned')
                        : t('campaign.history.lost'),
                  })}
                </li>
              ))}
          </ul>
        </section>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-danger btn-small" onClick={onReset}>
          {t('campaign.resetProfile')}
        </button>
      </div>
    </div>
  );
}

function Fielding({
  profile,
  run,
  onField,
  onAbandon,
}: {
  profile: Profile;
  run: RunState;
  onField: (f: FieldedPawn[]) => void;
  onAbandon: () => void;
}) {
  const [files, setFiles] = useState<(string | '')[]>(() =>
    run.roster.slice(0, 6).map((r) => r.id),
  );
  const used = new Set(files.filter(Boolean));
  const complete = files.every(Boolean) && used.size === 6;
  const reserves = run.roster.filter((r) => !used.has(r.id));
  const deck = runDeck(run);
  return (
    <div className="fielding">
      <section className="panel">
        <h2>{t('campaign.table', { table: run.table, tables: C.CAMPAIGN_TABLES })}</h2>
        <p>{t('campaign.regular', { name: regularName(run.table) })}</p>
        <p>{t('campaign.headStart', { n: headStartFor(run.table) })}</p>
        <p className="dim">{t('campaign.brokerWinsTies')}</p>
        <p className="dim">
          {t('campaign.deck', { n: deck.length })}
          {run.bought.length > 0
            ? ` · ${t('campaign.bought', { cards: run.bought.map(specName).join(', ') })}`
            : ''}
        </p>
      </section>
      <section className="panel">
        <h2>{t('campaign.field')}</h2>
        <p className="dim">{t('campaign.fieldHint')}</p>
        <div className="file-grid">
          {FILES.split('').map((f, i) => (
            <label key={f} className="field">
              <span className="field-label">{t('campaign.file', { file: f })}</span>
              <select
                value={files[i] ?? ''}
                onChange={(e) =>
                  setFiles((cur) =>
                    cur.map((x, j) => (j === i ? e.target.value : x === e.target.value ? '' : x)),
                  )
                }
              >
                <option value="">—</option>
                {run.roster.map((r) => (
                  <option key={r.id} value={r.id}>
                    {pawnName(profile, r.id, r.standIn)}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        <p className="dim">
          {t('campaign.reserve')}:{' '}
          {reserves.length
            ? reserves.map((r) => pawnName(profile, r.id, r.standIn)).join(', ')
            : '—'}
        </p>
        <div className="form-actions">
          <button
            type="button"
            className="btn"
            onClick={() => setFiles(run.roster.slice(0, 6).map((r) => r.id))}
          >
            {t('campaign.fieldAuto')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-large"
            disabled={!complete}
            onClick={() => onField(files.map((id, file) => ({ id: id as string, file })))}
          >
            {t('campaign.play')}
          </button>
        </div>
      </section>
      <div className="form-actions">
        <button type="button" className="btn btn-danger btn-small" onClick={onAbandon}>
          {t('campaign.abandonRun')}
        </button>
      </div>
    </div>
  );
}

function TableResult({
  profile,
  outcome,
  onContinue,
}: {
  profile: Profile;
  outcome: TableOutcome;
  onContinue: () => void;
}) {
  const names = (ids: readonly string[]) =>
    ids.map((id) => profile.pawns[id]?.name ?? id).join(', ');
  return (
    <section className={`panel table-result ${outcome.won ? 'is-won' : 'is-lost'}`}>
      <h2>{outcome.won ? t('campaign.won.title') : t('campaign.lost.title')}</h2>
      <p>{t('campaign.tableScore', { s: outcome.scores.S, n: outcome.scores.N })}</p>
      <p className="dim">{t(`dialog.matchEnd.reason.${outcome.result.reason}`)}</p>
      {outcome.won ? (
        <>
          {outcome.reclaimed.length > 0 && (
            <p>{t('campaign.reclaimed', { names: names(outcome.reclaimed) })}</p>
          )}
          {outcome.recruited.length > 0 && (
            <p>{t('campaign.recruited', { names: names(outcome.recruited) })}</p>
          )}
        </>
      ) : outcome.lost.length + outcome.vanished.length === 0 ? (
        <p>{t('campaign.noLosses')}</p>
      ) : (
        <>
          {outcome.lost.length > 0 && (
            <p className="warn">{t('campaign.lostPawns', { names: names(outcome.lost) })}</p>
          )}
          {outcome.vanished.length > 0 && (
            <p className="dim">{t('campaign.vanished', { n: outcome.vanished.length })}</p>
          )}
        </>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-primary" autoFocus onClick={onContinue}>
          {t('action.next')}
        </button>
      </div>
    </section>
  );
}

function Counter({
  profile,
  run,
  onLeave,
  onAbandon,
}: {
  profile: Profile;
  run: RunState;
  onLeave: (p: Purchase[]) => void;
  onAbandon: () => void;
}) {
  const [payers, setPayers] = useState<string[]>(() => run.offers.map(() => ''));
  const tradeable = tradeablePawns(profile);
  const purchases: Purchase[] = payers.flatMap((id, offer) => (id ? [{ offer, payWith: id }] : []));
  const tooMany = run.roster.length - purchases.length < C.CAMPAIGN_FIELDED;
  const outcome = run.outcomes[run.outcomes.length - 1];
  const summary = useMemo(() => outcome, [outcome]);
  return (
    <section className="panel counter">
      {summary && <TableSummary profile={profile} outcome={summary} />}
      <h2>{t('campaign.counter')}</h2>
      <p className="dim">{t('campaign.counterHint', { n: C.CAMPAIGN_FIELDED })}</p>
      <ul className="offer-list">
        {run.offers.map((spec, i) => (
          <li key={i} className="offer">
            <CardView
              size="md"
              face={spec.kind === 'normal' ? spec.face : null}
              wild={spec.kind === 'wild'}
              label={specName(spec)}
            />
            <label className="field">
              <span className="field-label">{t('campaign.payWith')}</span>
              <select
                value={payers[i] ?? ''}
                onChange={(e) =>
                  setPayers((cur) =>
                    cur.map((x, j) => (j === i ? e.target.value : x === e.target.value ? '' : x)),
                  )
                }
              >
                <option value="">{t('campaign.dontBuy')}</option>
                {tradeable.map((id) => (
                  <option key={id} value={id}>
                    {profile.pawns[id]?.name ?? id}
                  </option>
                ))}
              </select>
            </label>
          </li>
        ))}
      </ul>
      {tooMany && <p className="warn">{t('campaign.counterHint', { n: C.CAMPAIGN_FIELDED })}</p>}
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-primary btn-large"
          disabled={tooMany}
          onClick={() => onLeave(purchases)}
        >
          {t('campaign.leaveCounter')}
        </button>
        <button type="button" className="btn btn-danger btn-small" onClick={onAbandon}>
          {t('campaign.abandonRun')}
        </button>
      </div>
    </section>
  );
}

function TableSummary({ profile, outcome }: { profile: Profile; outcome: TableOutcome }) {
  const names = (ids: readonly string[]) =>
    ids.map((id) => profile.pawns[id]?.name ?? id).join(', ');
  return (
    <div className="table-summary">
      <p>
        <strong>{t('campaign.won.title')}</strong> ·{' '}
        {t('campaign.tableScore', { s: outcome.scores.S, n: outcome.scores.N })}
      </p>
      {outcome.reclaimed.length > 0 && (
        <p>{t('campaign.reclaimed', { names: names(outcome.reclaimed) })}</p>
      )}
      {outcome.recruited.length > 0 && (
        <p>{t('campaign.recruited', { names: names(outcome.recruited) })}</p>
      )}
    </div>
  );
}

function RunEnd({
  profile,
  run,
  onClose,
}: {
  profile: Profile;
  run: RunState;
  onClose: () => void;
}) {
  const last = run.outcomes[run.outcomes.length - 1];
  const names = (ids: readonly string[]) =>
    ids.map((id) => profile.pawns[id]?.name ?? id).join(', ');
  return (
    <section className={`panel run-end ${run.phase === 'won' ? 'is-won' : 'is-lost'}`}>
      <h2>{run.phase === 'won' ? t('campaign.runWon') : t('campaign.runLost')}</h2>
      <ol className="run-recap">
        {run.outcomes.map((o) => (
          <li key={o.table} className={o.won ? 'is-won' : 'is-lost'}>
            {t(o.won ? 'campaign.recap.won' : 'campaign.recap.lost', {
              table: o.table,
              name: regularName(o.table),
              s: o.scores.S,
              n: o.scores.N,
            })}
          </li>
        ))}
      </ol>
      {last &&
        !last.won &&
        (last.lost.length > 0 ? (
          <p className="warn">{t('campaign.lostPawns', { names: names(last.lost) })}</p>
        ) : (
          <p>{t('campaign.noLosses')}</p>
        ))}
      {last && last.won && last.reclaimed.length > 0 && (
        <p>{t('campaign.reclaimed', { names: names(last.reclaimed) })}</p>
      )}
      {last && last.won && last.recruited.length > 0 && (
        <p>{t('campaign.recruited', { names: names(last.recruited) })}</p>
      )}
      {run.bought.length > 0 && (
        <p className="dim">
          {t('campaign.bought', { cards: run.bought.map(specName).join(', ') })}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-primary" autoFocus onClick={onClose}>
          {t('campaign.closeRun')}
        </button>
      </div>
    </section>
  );
}
